<template>
  <div class="lp-grid-root">
    <!-- ──── HEADER ROW (sticky) ──── -->
    <div class="lp-grid-header">
      <div class="lp-col-expand"></div>
      <div class="lp-col-check" @click.stop>
        <input
          type="checkbox"
          class="gcorp-checkbox"
          :checked="headerCheckState.checked"
          v-indeterminate="headerCheckState.indeterminate"
          @change="onHeaderCheckChange"
        />
      </div>
      <div class="lp-col-product">Product / Description</div>
      <div class="lp-col-mfg">Mfg Date</div>
      <div class="lp-col-bb">Best Before</div>
      <div class="lp-col-rcvd">Received</div>
      <div v-if="quantityMode" class="lp-col-numlabels"># Labels</div>
      <div v-if="quantityMode" class="lp-col-unitqty">Unit Qty</div>
      <div v-if="quantityMode" class="lp-col-calcmethod">Calc Method</div>
      <div class="lp-col-printqty">Print Qty</div>
    </div>

    <!-- ──── GRID BODY (scrollable) ──── -->
    <div class="lp-grid-body">
      <div
        class="lp-product-group"
        v-for="(product, pIdx) in rows"
        :key="product.ItemCode + '-' + pIdx"
      >
        <!-- PARENT ROW -->
        <div
          class="lp-row lp-parent-row"
          :class="{
            'is-expanded': product._expanded && product.IsBatchControlled,
            'no-expand': !product.IsBatchControlled,
          }"
          @click="product.IsBatchControlled ? toggleExpand(product) : null"
        >
          <!-- Expand chevron -->
          <div class="lp-col-expand">
            <i
              v-if="product.IsBatchControlled"
              class="dx-icon dx-icon-chevronright lp-chevron"
              :class="{ 'is-open': product._expanded }"
            ></i>
            <span v-else class="lp-dash">—</span>
          </div>

          <!-- Parent checkbox -->
          <div class="lp-col-check" @click.stop>
            <input
              type="checkbox"
              class="gcorp-checkbox"
              :checked="parentCheckState(product).checked"
              v-indeterminate="parentCheckState(product).indeterminate"
              @change="onParentCheckChange(product, $event)"
            />
          </div>

          <!-- Product info -->
          <div class="lp-col-product">
            <div class="lp-product-name">{{ product.ItemDescription }}</div>
            <div class="lp-product-meta">
              <span>{{ product.ItemCode }}</span>
              <span class="lp-pill lp-pill-grey">
                <span v-if="product.IsBatchControlled">
                  {{ product.Batches.length }} batch{{
                    product.Batches.length === 1 ? "" : "es"
                  }}
                </span>
                <span v-else>Non-batch</span>
              </span>
              <span v-if="product.IsAggregated" class="lp-pill lp-pill-blue"
                >Lines aggregated</span
              >
            </div>
          </div>

          <!-- Mfg / Best Before / Received cells for parent row -->
          <div class="lp-col-mfg lp-dash">—</div>
          <div class="lp-col-bb lp-dash">—</div>

          <!-- Received quantity: individual for non-batch, total for batch -->
          <div class="lp-col-rcvd">
            <span v-if="!product.IsBatchControlled">{{
              formatQuantity(product.Batches[0].ReceivedQuantity)
            }}</span>
            <span v-else>{{
              formatQuantity(getTotalReceivedQty(product))
            }}</span>
          </div>

          <!-- QM columns (parent row) -->
          <template v-if="quantityMode">
            <!-- For batch-controlled: show dashes; for non-batch: show controls -->
            <div
              v-if="product.IsBatchControlled"
              class="lp-col-numlabels lp-dash"
            >
              —
            </div>
            <div v-else class="lp-col-numlabels" @click.stop>
              <DxNumberBox
                v-model="product.Batches[0]._numLabels"
                :min="0"
                :disabled="product.Batches[0]._calcMethod === 'PalletSplit'"
                class="gcorp-grid-input"
                @value-changed="
                  onNumLabelsChange(product.Batches[0], $event.value)
                "
                format="###,###,##0"
              />
            </div>

            <div v-if="product.IsBatchControlled" class="lp-col-unitqty">
              <span class="lp-batch-total-qty">{{
                formatQuantity(getTotalUnitQty(product))
              }}</span>
            </div>
            <div v-else class="lp-col-unitqty">
              <span class="lp-batch-total-qty">{{
                formatQuantity(product.Batches[0]._unitQty)
              }}</span>
            </div>

            <div
              v-if="product.IsBatchControlled"
              class="lp-col-calcmethod lp-dash"
            >
              —
            </div>
            <div v-else class="lp-col-calcmethod" @click.stop>
              <div class="lp-calc-method-toggle">
                <button
                  :class="[
                    'lp-calc-btn',
                    {
                      'is-active':
                        product.Batches[0]._calcMethod === 'Proportional',
                    },
                  ]"
                  @click="
                    onCalcMethodClick(
                      product,
                      product.Batches[0],
                      'Proportional',
                    )
                  "
                >
                  Prop.
                </button>
                <button
                  :class="[
                    'lp-calc-btn',
                    {
                      'is-active':
                        product.Batches[0]._calcMethod === 'PalletSplit',
                    },
                  ]"
                  @click="
                    onCalcMethodClick(
                      product,
                      product.Batches[0],
                      'PalletSplit',
                    )
                  "
                >
                  Pallet
                </button>
              </div>
              <span
                v-if="product.Batches[0]._palletSplitError"
                class="lp-calc-error"
              >
                No Cartons/Pallet configured
              </span>
            </div>
          </template>

          <!-- Print Qty: input for non-batch in standard mode, read-only total in QM or batch mode -->
          <div class="lp-col-printqty" @click.stop>
            <DxNumberBox
              v-if="!product.IsBatchControlled && !quantityMode"
              v-model="product.Batches[0]._printQty"
              :min="0"
              class="gcorp-grid-input"
              @value-changed="
                onPrintQtyChange(product.Batches[0], $event.value)
              "
              format="###,###,##0"
            />
            <span v-else class="lp-batch-total-qty">{{
              getTotalPrintQty(product)
            }}</span>
          </div>
        </div>
        <!-- /parent row -->

        <!-- LABEL ROWS for non-batch products (directly under product, no batch row) -->
        <template v-if="quantityMode && !product.IsBatchControlled">
          <div
            v-for="label in product.Batches[0]._labels"
            :key="
              'label-' +
              product.ItemCode +
              '-' +
              product.Batches[0]._calcId +
              '-' +
              label.LabelNo
            "
            class="lp-row lp-label-row"
          >
            <!-- spacers matching column layout -->
            <div class="lp-col-expand"></div>
            <div class="lp-col-check"></div>
            <div class="lp-col-product lp-label-num">
              Label {{ label.LabelNo }}
            </div>
            <div class="lp-col-mfg lp-dash">—</div>
            <div class="lp-col-bb lp-dash">—</div>
            <div class="lp-col-rcvd lp-dash">—</div>
            <div class="lp-col-numlabels lp-dash">—</div>
            <!-- Unit Qty (editable) -->
            <div class="lp-col-unitqty" @click.stop>
              <DxNumberBox
                v-model="label.Quantity"
                :min="0"
                class="gcorp-grid-input"
                @value-changed="
                  onLabelQtyChange(product.Batches[0], label, $event.value)
                "
                :format="'###,###,##0.0000'"
                width="125"
              />
            </div>
            <div class="lp-col-calcmethod lp-dash">—</div>
            <!-- Print Qty (editable, cascades to product level) -->
            <div class="lp-col-printqty" @click.stop>
              <DxNumberBox
                v-model="label._printQty"
                :min="0"
                class="gcorp-grid-input"
                @value-changed="
                  onLabelPrintQtyChange(product.Batches[0], label, $event.value)
                "
                format="###,###,##0"
                width="100"
              />
            </div>
          </div>
        </template>

        <!-- BATCH ROWS (only for batch-controlled products) -->
        <div
          v-if="product.IsBatchControlled"
          class="lp-batch-rows"
          v-show="product._expanded"
        >
          <template v-for="(batch, bIdx) in product.Batches">
            <div
              class="lp-row lp-batch-row"
              :key="'batch-' + batch.BatchNumber + '-' + bIdx"
            >
              <!-- Expand spacer -->
              <div class="lp-col-expand"></div>

              <!-- Batch checkbox -->
              <div class="lp-col-check" @click.stop>
                <input
                  type="checkbox"
                  class="gcorp-checkbox"
                  :checked="batch._selected"
                  @change="
                    onBatchCheckChange(product, batch, $event.target.checked)
                  "
                />
              </div>

              <!-- Batch number -->
              <div class="lp-col-product lp-batch-num">
                {{ batch.BatchNumber }}
              </div>

              <!-- Manufacturing date -->
              <div class="lp-col-mfg lp-batch-date">
                {{
                  batch.ManufacturingDate
                    ? new Date(batch.ManufacturingDate).toShortDateString()
                    : "—"
                }}
              </div>

              <!-- Best before date -->
              <div class="lp-col-bb lp-batch-date">
                {{
                  batch.BestBeforeDate
                    ? new Date(batch.BestBeforeDate).toShortDateString()
                    : batch.BatchUseByDate
                    ? new Date(batch.BatchUseByDate).toShortDateString()
                    : "—"
                }}
              </div>

              <!-- Received quantity -->
              <div class="lp-col-rcvd lp-batch-rcvd">
                {{ formatQuantity(batch.ReceivedQuantity) }}
              </div>

              <!-- QM columns for batch row -->
              <div v-if="quantityMode" class="lp-col-numlabels" @click.stop>
                <DxNumberBox
                  v-model="batch._numLabels"
                  :min="0"
                  :disabled="batch._calcMethod === 'PalletSplit'"
                  class="gcorp-grid-input"
                  @value-changed="onNumLabelsChange(batch, $event.value)"
                />
              </div>
              <div v-if="quantityMode" class="lp-col-unitqty">
                <span class="lp-batch-total-qty">{{
                  formatQuantity(batch._unitQty)
                }}</span>
              </div>
              <div v-if="quantityMode" class="lp-col-calcmethod" @click.stop>
                <div class="lp-calc-method-toggle">
                  <button
                    :class="[
                      'lp-calc-btn',
                      { 'is-active': batch._calcMethod === 'Proportional' },
                    ]"
                    @click="onCalcMethodClick(product, batch, 'Proportional')"
                  >
                    Prop.
                  </button>
                  <button
                    :class="[
                      'lp-calc-btn',
                      { 'is-active': batch._calcMethod === 'PalletSplit' },
                    ]"
                    @click="onCalcMethodClick(product, batch, 'PalletSplit')"
                  >
                    Pallet
                  </button>
                </div>
                <span v-if="batch._palletSplitError" class="lp-calc-error">
                  No Cartons/Pallet configured
                </span>
              </div>

              <!-- Print Qty: editable in standard mode, read-only total in QM mode -->
              <div class="lp-col-printqty" @click.stop>
                <DxNumberBox
                  v-if="!quantityMode"
                  v-model="batch._printQty"
                  :min="0"
                  class="gcorp-grid-input"
                  @value-changed="onPrintQtyChange(batch, $event.value)"
                />
                <span v-else class="lp-batch-total-qty">{{
                  formatQuantity(getParentPrintQty(batch), "##,###,##0")
                }}</span>
              </div>
            </div>

            <!-- LABEL ROWS (in Quantity Mode only) -->
            <div
              v-if="quantityMode"
              :key="'labels-' + batch.BatchNumber + '-' + bIdx"
              class="lp-label-rows"
            >
              <div
                class="lp-row lp-label-row"
                v-for="label in batch._labels"
                :key="
                  'label-' +
                  batch.BatchNumber +
                  '-' +
                  batch._calcId +
                  '-' +
                  label.LabelNo
                "
              >
                <!-- spacers matching column layout -->
                <div class="lp-col-expand"></div>
                <div class="lp-col-check"></div>
                <div class="lp-col-product lp-label-num">
                  Label {{ label.LabelNo }}
                </div>
                <div class="lp-col-mfg lp-dash">—</div>
                <div class="lp-col-bb lp-dash">—</div>
                <div class="lp-col-rcvd lp-dash">—</div>
                <div class="lp-col-numlabels lp-dash">—</div>
                <!-- Unit Qty (editable) -->
                <div class="lp-col-unitqty" @click.stop>
                  <DxNumberBox
                    v-model="label.Quantity"
                    :min="0"
                    class="gcorp-grid-input"
                    @value-changed="
                      onLabelQtyChange(batch, label, $event.value)
                    "
                    :format="'###,###,##0.0000'"
                    width="125"
                  />
                </div>
                <div class="lp-col-calcmethod lp-dash">—</div>
                <!-- Print Qty (editable, cascades to batch and product levels) -->
                <div class="lp-col-printqty" @click.stop>
                  <DxNumberBox
                    v-model="label._printQty"
                    :min="0"
                    class="gcorp-grid-input"
                    @value-changed="
                      onLabelPrintQtyChange(batch, label, $event.value)
                    "
                    format="###,###,##0"
                    width="100"
                  />
                </div>
              </div>
            </div>
          </template>
        </div>
        <!-- /batch rows -->
      </div>
      <!-- /product group -->
    </div>
    <!-- /grid body -->

    <!-- ──── FOOTER ──── -->
    <div class="lp-grid-footer">
      <DxButton text="Cancel" styling-mode="outlined" @click="onCancel" />
      <button class="lp-print-btn" :disabled="!canPrint" @click="onPrint">
        <i class="dx-icon dx-icon-print"></i>
        <span>Print</span>
        <span class="lp-print-badge" v-show="totalSelectedCount > 0">
          {{ totalSelectedCount }}
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import DxNumberBox from "devextreme-vue/number-box";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcLabelPrintingGrid",
  components: {
    DxButton,
    DxNumberBox,
  },
  props: {
    products: {
      type: Array,
      default: function () {
        return [];
      },
    },
    quantityMode: {
      type: Boolean,
      default: false,
    },
    defaultCalcMethod: {
      type: String,
      default: null,
    },
  },
  data: function () {
    return {
      rows: [],
    };
  },
  computed: {
    totalSelectedCount: function () {
      var count = 0;
      if (!this.rows || this.rows.length === 0) return 0;
      this.rows.forEach(function (product) {
        if (product && product.Batches) {
          product.Batches.forEach(function (batch) {
            if (batch._selected && batch._printQty > 0) {
              count += batch._printQty;
            }
          });
        }
      });
      return count;
    },
    canPrint: function () {
      return this.totalSelectedCount > 0;
    },
    headerCheckState: function () {
      var total = 0;
      var selected = 0;
      if (!this.rows || this.rows.length === 0) {
        return { checked: false, indeterminate: false };
      }
      this.rows.forEach(function (product) {
        if (product && product.Batches) {
          product.Batches.forEach(function (batch) {
            total++;
            if (batch._selected) {
              selected++;
            }
          });
        }
      });
      return {
        checked: total > 0 && selected === total,
        indeterminate: selected > 0 && selected < total,
      };
    },
  },
  directives: {
    indeterminate: {
      bind: function (el, binding) {
        el.indeterminate = binding.value;
      },
      update: function (el, binding) {
        el.indeterminate = binding.value;
      },
    },
  },
  watch: {
    products: function (val) {
      this.buildRows(val);
    },
  },
  created: function () {
    this.buildRows(this.products);
  },
  mounted: function () {
    var self = this;
    this.$nextTick(function () {
      if (self.$el && typeof self.$el.querySelector === "function") {
        var gridBody = self.$el.querySelector(".lp-grid-body");
        var gridHeader = self.$el.querySelector(".lp-grid-header");
        if (gridBody) {
          gridBody.addEventListener("wheel", function (e) {
            e.preventDefault();
            // Vertical scroll: deltaY or Shift+wheel
            if (e.shiftKey || e.deltaX !== 0) {
              gridBody.scrollLeft += e.shiftKey ? e.deltaY : e.deltaX;
            } else {
              gridBody.scrollTop += e.deltaY;
            }
          });
          // Sync header horizontal scroll with body using transform
          gridBody.addEventListener("scroll", function () {
            if (gridHeader) {
              gridHeader.style.transform =
                "translateX(-" + gridBody.scrollLeft + "px)";
            }
          });
        }
      }
    });
  },
  methods: {
    buildRows: function (products) {
      var self = this;
      this.rows = (products || []).map(function (product) {
        var batches = (product.Batches || []).map(function (batch) {
          var batchObj = {
            _selected: false,
            _printQty: 0,
            _unitQty: 0,
            // QM fields
            _numLabels: 1,
            _calcMethod: "Proportional",
            _labels: [{ LabelNo: 1, Quantity: 0, _printQty: 0 }],
            _calcId: 0, // incremented each time labels are recalculated
            CartonsPerPallet: batch.CartonsPerPallet || null,
            PriorPrint: batch.PriorPrint || null,
            _userModified: false, // tracks if user has manually entered data
            _palletSplitError: false,
          };

          // Pre-fill from PriorPrint if available (regardless of QM state)
          // Will be used when user enables QM
          if (batch.PriorPrint) {
            var prior = batch.PriorPrint;
            batchObj._numLabels = (prior.Labels || []).length;
            // Use CalcMethod if available, otherwise default to Proportional
            if (prior.CalcMethod) {
              batchObj._calcMethod = prior.CalcMethod;
            } else {
              batchObj._calcMethod = "Proportional";
            }
            batchObj._labels = (prior.Labels || []).map(function (label) {
              return {
                LabelNo: label.LabelNo,
                Quantity: label.Quantity || 0,
                _printQty: label.LabelsToPrint || 0,
              };
            });
            // Cascade pre-filled values to batch level
            batchObj._unitQty = batchObj._labels.reduce(function (sum, l) {
              return sum + (l.Quantity || 0);
            }, 0);
            batchObj._printQty = batchObj._labels.reduce(function (sum, l) {
              return sum + (l._printQty || 0);
            }, 0);
            // Auto-select batch if it has print qty
            if (batchObj._printQty > 0) {
              batchObj._selected = true;
            }
          }

          // Task 71417: apply default calc method for fresh lines on initial load (QM on, no prior history)
          if (!batch.PriorPrint && self.defaultCalcMethod) {
            batchObj._calcMethod = self.defaultCalcMethod;
            if (self.defaultCalcMethod === "Proportional") {
              var defaultLabels = self.calcProportional(
                batch.ReceivedQuantity,
                batchObj._numLabels,
              );
              batchObj._labels = defaultLabels;
              batchObj._numLabels = defaultLabels.length;
              batchObj._unitQty = self.getLabelUnitQty(batchObj);
              // _printQty stays 0, _selected stays false — user decides what to print
            } else if (self.defaultCalcMethod === "PalletSplit") {
              if (batchObj.CartonsPerPallet > 0) {
                var palletLabels = self.calcPalletSplit(
                  batch.ReceivedQuantity,
                  batchObj.CartonsPerPallet,
                );
                batchObj._labels = palletLabels;
                batchObj._numLabels = palletLabels.length;
                batchObj._unitQty = self.getLabelUnitQty(batchObj);
              } else {
                batchObj._palletSplitError = true;
              }
            }
          }

          // Ensure labels are always numbered sequentially from 1 to N
          batchObj._labels.forEach(function (label, idx) {
            label.LabelNo = idx + 1;
          });

          return Object.assign({}, batch, batchObj);
        });
        return Object.assign({}, product, {
          _expanded: true,
          _selected: false,
          Batches: batches,
        });
      });
    },
    toggleExpand: function (product) {
      product._expanded = !product._expanded;
    },
    parentCheckState: function (product) {
      var selected = 0;
      product.Batches.forEach(function (batch) {
        if (batch._selected) {
          selected++;
        }
      });
      return {
        checked:
          selected === product.Batches.length && product.Batches.length > 0,
        indeterminate: selected > 0 && selected < product.Batches.length,
      };
    },
    getTotalReceivedQty: function (product) {
      var total = 0;
      product.Batches.forEach(function (batch) {
        total += batch.ReceivedQuantity || 0;
      });
      return total;
    },
    getTotalPrintQty: function (product) {
      var total = 0;
      product.Batches.forEach(function (batch) {
        if (batch._selected && batch._printQty > 0) {
          total += batch._printQty;
        }
      });
      return formatNumber(total, "##,###,###,###,##0");
    },
    getTotalLabelQty: function (product) {
      var total = 0;
      product.Batches.forEach(function (batch) {
        total += (batch._labels || []).reduce(function (sum, l) {
          return sum + (l.Quantity || 0);
        }, 0);
      });
      return total;
    },
    getTotalUnitQty: function (product) {
      var total = 0;
      product.Batches.forEach(function (batch) {
        total += batch._unitQty || 0;
      });
      return total;
    },
    formatQuantity: function (value, format = "##,###,###,###,##0.0000") {
      if (!value && value !== 0) return "—";
      return formatNumber(value, format);
    },
    getLabelUnitQty: function (batch) {
      return batch._labels.reduce(function (sum, l) {
        return sum + (l.Quantity || 0);
      }, 0);
    },
    getParentPrintQty: function (batch) {
      return batch._labels.reduce(function (sum, l) {
        return sum + (l._printQty || 0);
      }, 0);
    },
    calcProportional: function (Q, N) {
      var base = Math.floor((Q / N) * 100) / 100;
      var remainder = +(Q - base * N).toFixed(2);
      return Array.from({ length: N }, function (_, i) {
        return {
          LabelNo: i + 1,
          Quantity: i === N - 1 ? base + remainder : base,
          _printQty: 0,
        };
      });
    },
    calcPalletSplit: function (Q, C) {
      var fullLabels = Math.floor(Q / C);
      var remainder = +(Q - fullLabels * C).toFixed(2);
      var N = remainder > 0 ? fullLabels + 1 : fullLabels;
      return Array.from({ length: N }, function (_, i) {
        return {
          LabelNo: i + 1,
          Quantity: remainder > 0 && i === N - 1 ? remainder : C,
          _printQty: 0,
        };
      });
    },
    applyCalcMethod: async function (product, batch, method) {
      batch._userModified = true;
      batch._palletSplitError = false;
      // await this.$nextTick(); // Ensure any pending UI updates are processed before calculations
      if (method === "Proportional") {
        var propLabels = this.calcProportional(
          batch.ReceivedQuantity,
          batch._numLabels,
        );
        this.$set(batch, "_labels", propLabels);
        batch._calcMethod = "Proportional";
        batch._calcId++;
        batch._unitQty = this.getLabelUnitQty(batch);
        batch._printQty = this.getParentPrintQty(batch);
        if (batch._printQty === 0) {
          batch._selected = false;
        }
      } else if (method === "PalletSplit") {
        if (!batch.CartonsPerPallet || batch.CartonsPerPallet <= 0) {
          batch._calcMethod = null;
          batch._palletSplitError = true;
          return;
        }
        batch._palletSplitError = false;
        var newLabels = this.calcPalletSplit(
          batch.ReceivedQuantity,
          batch.CartonsPerPallet,
        );
        this.$set(batch, "_labels", newLabels);
        batch._calcMethod = "PalletSplit"; // SET FIRST: prevents onNumLabelsChange re-calculation when _numLabels updates
        batch._suppressNumLabelsChange = true; // Guard: prevent onNumLabelsChange from recalculating
        batch._numLabels = newLabels.length; // This change triggers @value-changed, but guard prevents recalculation
        batch._calcId++;
        batch._unitQty = this.getLabelUnitQty(batch);
        batch._printQty = this.getParentPrintQty(batch);
        if (batch._printQty === 0) {
          batch._selected = false;
        }
        // Clear guard AFTER the DxNumberBox event fires (use $nextTick to defer)
        this.$nextTick(function () {
          batch._suppressNumLabelsChange = false;
        });
      }
      // When # Labels changes without an active calculation method, expand/trim array for manual entry
      if (
        method !== "Proportional" &&
        method !== "PalletSplit" &&
        batch._labels.length !== batch._numLabels
      ) {
        var currentLen = batch._labels.length;
        if (batch._numLabels > currentLen) {
          for (var i = currentLen; i < batch._numLabels; i++) {
            batch._labels.push({ LabelNo: i + 1, Quantity: 0, _printQty: 0 });
          }
        } else if (batch._numLabels < currentLen) {
          batch._labels.splice(batch._numLabels);
        }
        // Renumber all labels to ensure they're sequential from 1 to N
        batch._labels.forEach(function (label, idx) {
          label.LabelNo = idx + 1;
        });
        batch._unitQty = this.getLabelUnitQty(batch);
      }
    },
    onCalcMethodClick: async function (product, batch, method) {
      // Preserve parent's current Print Qty to distribute among regenerated labels
      var previousPrintQty = batch._printQty || 0;
      var self = this;

      // Apply the calc method (regenerates labels with _printQty: 0)
      await this.applyCalcMethod(product, batch, method);

      // After labels are regenerated, distribute the previous Print Qty among them
      this.$nextTick(function () {
        if (batch._labels && batch._labels.length > 0 && previousPrintQty > 0) {
          // Distribute using floor + remainder to avoid fractional values that DevExtreme
          // format "###,###,##0" would round, causing qty loss (e.g. 10 / 3 = 3.33 → displayed
          // as 3 → value-changed fires with 3 → 3+3+3 = 9, losing 1)
          var labelCount = batch._labels.length;
          var baseQty = Math.floor(previousPrintQty / labelCount);
          var remainder = previousPrintQty - baseQty * labelCount;
          batch._labels.forEach(function (label, i) {
            self.$set(label, "_printQty", baseQty + (i < remainder ? 1 : 0));
          });
          // Recalculate batch total from newly distributed label quantities
          batch._printQty = self.getParentPrintQty(batch);
          // Auto-select batch if it has print qty
          if (batch._printQty > 0) {
            batch._selected = true;
          }
        }

        // Sync parent selection state
        this.syncParentState(product);
      });
    },
    onNumLabelsChange: function (batch, N) {
      var self = this;
      // GUARD: Skip recalculation if applyCalcMethod is programmatically updating _numLabels
      if (batch._suppressNumLabelsChange) {
        return;
      }
      batch._userModified = true;
      batch._numLabels = Math.max(0, N);
      var isQMMethod =
        batch._calcMethod === "Proportional" ||
        batch._calcMethod === "PalletSplit";

      if (batch._calcMethod === "PalletSplit") {
        // PalletSplit recomputes count from Q/C and would overwrite _numLabels.
        // Redistribute proportionally using the user's requested count instead.
        var propLabels = this.calcProportional(
          batch.ReceivedQuantity,
          batch._numLabels,
        );
        this.$set(batch, "_labels", propLabels);
        batch._calcId++;
        batch._unitQty = this.getLabelUnitQty(batch);
      } else {
        this.applyCalcMethod(null, batch, batch._calcMethod);
      }

      // For QM calc methods, default regenerated labels to Print Qty 1 so the
      // batch stays actionable without the user needing to re-enter quantities.
      if (isQMMethod) {
        this.$nextTick(function () {
          if (batch._labels && batch._labels.length > 0) {
            batch._labels.forEach(function (label) {
              self.$set(label, "_printQty", 1);
            });
            batch._printQty = self.getParentPrintQty(batch);
            if (batch._printQty > 0) {
              batch._selected = true;
            }
          }
        });
      }
    },
    onLabelQtyChange: function (batch, label, value) {
      batch._userModified = true;
      label.Quantity = value;
      // Update batch Unit Qty (sum of all label unit quantities)
      batch._unitQty = this.getLabelUnitQty(batch);
    },
    onLabelPrintQtyChange: function (batch, label, value) {
      batch._userModified = true;
      label._printQty = value;
      // Update batch Print Qty (sum of all label print quantities)
      batch._printQty = this.getParentPrintQty(batch);
      // Auto-select/deselect batch based on Print Qty
      if (batch._printQty === 0) {
        batch._selected = false;
      } else if (batch._printQty > 0) {
        batch._selected = true;
      }
    },
    onBatchCheckChange: function (product, batch, value) {
      batch._userModified = true;
      batch._selected = value;
      if (value && batch._printQty === 0) {
        // Auto-set Print Qty to 1 when checkbox is ticked with zero quantity
        batch._printQty = 1;
      } else if (!value && batch._printQty > 0) {
        // Clear Print Qty when checkbox is unticked
        batch._printQty = 0;
      }
      if (this.quantityMode) {
        batch._labels.forEach(function (label) {
          label._selected = value;
          // In QM, cascade Print Qty to labels
          if (value && batch._printQty > 0) {
            label._printQty = batch._printQty;
          } else if (!value) {
            label._printQty = 0;
          }
        });
      }
      this.syncParentState(product);
    },
    syncParentState: function (product) {
      var selected = product.Batches.filter(function (b) {
        return b._selected;
      }).length;
      product._selected =
        selected === product.Batches.length && product.Batches.length > 0;
    },
    onParentCheckChange: function (product, event) {
      var checked = event.target.checked;
      var self = this;
      product.Batches.forEach(function (batch) {
        batch._selected = checked;
        if (checked && batch._printQty === 0) {
          // Auto-set Print Qty to 1 when checkbox is ticked with zero quantity
          batch._printQty = 1;
        } else if (!checked && batch._printQty > 0) {
          // Clear Print Qty when checkbox is unticked
          batch._printQty = 0;
        }
        if (self.quantityMode) {
          batch._labels.forEach(function (label) {
            label._selected = checked;
            // In QM, cascade Print Qty to labels
            if (checked && batch._printQty > 0) {
              label._printQty = batch._printQty;
            } else if (!checked) {
              label._printQty = 0;
            }
          });
        }
      });
      product._selected = checked;
    },
    onHeaderCheckChange: function (event) {
      var checking = event.target.checked;
      var self = this;
      this.rows.forEach(function (product) {
        product.Batches.forEach(function (batch) {
          batch._selected = checking;
          if (checking && batch._printQty === 0) {
            // Auto-set Print Qty to 1 when checkbox is ticked with zero quantity
            batch._printQty = 1;
          } else if (!checking && batch._printQty > 0) {
            // Clear Print Qty when checkbox is unticked
            batch._printQty = 0;
          }
          if (self.quantityMode) {
            batch._labels.forEach(function (label) {
              label._selected = checking;
              // In QM, cascade Print Qty to labels
              if (checking && batch._printQty > 0) {
                label._printQty = batch._printQty;
              } else if (!checking) {
                label._printQty = 0;
              }
            });
          }
        });
      });
      this.rows.forEach(function (product) {
        self.syncParentState(product);
      });
    },
    onPrintQtyChange: function (batch, value) {
      batch._userModified = true;
      batch._printQty = value;
      if (batch._printQty === 0) {
        batch._selected = false;
      } else if (batch._printQty > 0) {
        batch._selected = true;
      }
      if (this.quantityMode) {
        batch._labels.forEach(function (label) {
          label._printQty = batch._printQty;
        });
      }
    },
    onPrint: function () {
      if (!this.canPrint) {
        return;
      }
      var lines = [];
      var self = this;
      this.rows.forEach(function (product) {
        product.Batches.forEach(function (batch) {
          if (batch._selected && batch._printQty > 0) {
            var line = {
              ItemCode: product.ItemCode,
              ItemDescription: product.ItemDescription,
              BatchNumber: batch.BatchNumber,
              ManufacturingDate: batch.ManufacturingDate,
              BestBeforeDate: batch.BestBeforeDate,
              ReceivedQuantity: batch.ReceivedQuantity,
              LabelsToPrint: batch._printQty,
              CartonsPerPallet: batch.CartonsPerPallet,
              PriorPrint: batch.PriorPrint,
            };
            if (self.quantityMode) {
              line.CalcMethod = batch._calcMethod;
              line.LabelPrintingLineLabels = batch._labels.map(function (
                label,
              ) {
                return {
                  LabelNo: label.LabelNo,
                  Quantity: label.Quantity,
                  LabelsToPrint: label._printQty || 0,
                };
              });
            }
            lines.push(line);
          }
        });
      });
      this.$emit("print", { lines: lines });
    },
    bulkSetCalcMethod: function (method) {
      var self = this;
      this.rows.forEach(function (product) {
        product.Batches.forEach(function (batch) {
          self.applyCalcMethod(product, batch, method);
          // Use $nextTick to ensure labels array update is processed before setting properties
          self.$nextTick(function () {
            // Reset Print Qty and auto-select batch and set Print Qty to 1 when applying calc method
            batch._selected = true;
            batch._labels.forEach(function (label) {
              self.$set(label, "_selected", true);
              if (!label._printQty || label._printQty === 0) {
                self.$set(label, "_printQty", 1);
              }
            });
            // Recalculate batch total from labels (for QM mode display)
            batch._printQty = self.getParentPrintQty(batch);
          });
        });
        self.syncParentState(product);
      });
    },
    bulkClearAll: function () {
      var checking = false;
      var self = this;
      this.rows.forEach(function (product) {
        product._selected = false;
        product.Batches.forEach(function (batch) {
          batch._selected = checking;
          batch._userModified = true;
          batch._printQty = 0;
          // Clear calc method so no button appears active
          batch._calcMethod = null;
          batch._palletSplitError = false;
          if (self.quantityMode) {
            // Reset labels to single empty label in manual entry state
            batch._numLabels = 1;
            batch._labels = [{ LabelNo: 1, Quantity: 0, _printQty: 0 }];
            batch._labels.forEach(function (label) {
              label._selected = checking;
              label._printQty = 0;
            });
          }
        });
      });
      this.rows.forEach(function (product) {
        self.syncParentState(product);
      });
    },
    onCancel: function () {
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss">
.lp-grid-root {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.5;
  background-color: white;

  // ── HEADER ROW ──
  .lp-grid-header {
    display: flex;
    align-items: center;
    background-color: hsla(0, 0%, 73.3%, 0.1607843137);
    border-bottom: 1px solid #e5e7eb;
    padding: 0 16px;
    min-height: 40px;
    position: sticky;
    top: 0;
    z-index: 10;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.54);
    flex-shrink: 0;
    font-size: 12px;
    overflow-x: scroll;
    overflow-y: hidden;
    width: 100%;
    min-width: min-content;
  }

  .lp-grid-header::-webkit-scrollbar {
    height: 0;
    display: none;
  }

  // ── GRID BODY ──
  .lp-grid-body {
    flex: 1;
    min-height: 0;
    overflow-y: scroll;
    overflow-x: scroll;
    pointer-events: auto;
    scrollbar-width: auto;
  }

  .lp-grid-body::-webkit-scrollbar {
    width: 8px;
  }

  .lp-grid-body::-webkit-scrollbar-track {
    background: #f3f4f6;
  }

  .lp-grid-body::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }

  .lp-grid-body::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }

  // ── PRODUCT GROUP ──
  .lp-product-group {
    border-bottom: 1px solid #eaecf0;

    &:last-child {
      border-bottom: none;
    }
  }

  // ── ROW LAYOUT (shared by header, parent row, batch row) ──
  .lp-row {
    display: flex;
    align-items: center;
    padding: 0 16px;
    gap: 0;
    width: 100%;
    min-width: min-content;
  }

  .lp-parent-row {
    min-height: 44px;
    background: #fff;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover:not(.no-expand) {
      background: #fafbfc;
    }

    &.is-expanded {
      background: #f0f9ff;
    }

    &.no-expand {
      cursor: default;

      &:hover {
        background: #fff;
      }
    }
  }

  .lp-batch-row {
    min-height: 40px;
    background: #fafbff;

    &:hover {
      background: #f5f7fb;
    }
  }

  // ── COLUMNS (widths applied to header and row cells) ──
  .lp-col-expand {
    width: 28px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lp-col-check {
    width: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lp-col-product {
    flex: 1;
    min-width: 300px;
    padding-right: 16px;
  }

  .lp-col-mfg,
  .lp-col-bb {
    width: 100px;
    flex-shrink: 0;
    text-align: center;
    padding: 0 8px;
  }

  .lp-col-rcvd {
    width: 125px;
    flex-shrink: 0;
    text-align: right;
    padding-right: 8px;
  }

  .lp-col-unitqty {
    width: 125px;
    flex-shrink: 0;
    text-align: right;
    padding-right: 8px;
    display: flex;
    justify-content: flex-end;
  }

  .lp-col-printqty {
    width: 105px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  // ── CHEVRON ICON ──
  .lp-chevron {
    display: inline-flex;
    transition: transform 0.15s ease;
    color: #667085;

    &.is-open {
      transform: rotate(90deg);
    }
  }

  .lp-dash {
    color: #d1d5db;
    font-weight: normal;
  }

  // ── PRODUCT CELL CONTENT ──
  .lp-product-name {
    color: #1f2937;
    margin-bottom: 6px;
    font-size: 12px;
  }

  .lp-product-meta {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    font-size: 11px;
    color: #6b7280;
  }

  .lp-batch-num {
    color: #1f2937;
    font-size: 12px;
  }

  .lp-batch-date {
    color: #6b7280;
    font-size: 12px;
  }

  .lp-batch-rcvd {
    text-align: right;
    padding-right: 8px;
    color: #6b7280;
    font-size: 12px;
  }

  // ── PILL BADGES ──
  .lp-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }

  .lp-pill-grey {
    background: #f3f4f6;
    color: #6b7280;
  }

  .lp-pill-blue {
    background: #dbeafe;
    color: #1e40af;
  }

  .lp-pill-green {
    background: #d1fae5;
    color: #065f46;
  }

  // ── CUSTOM CHECKBOX ──
  input.gcorp-checkbox {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid #d0d5dd;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;

    &:hover {
      border-color: #b3bac5;
    }

    &:checked,
    &:indeterminate {
      background: #35baf6;
      border-color: #2a95c5;
    }

    &:checked::after {
      content: "";
      position: relative;
      display: block;
      width: 4px;
      height: 9px;
      border: solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      margin-left: 5px;
      margin-top: 2px;
    }

    &:indeterminate::after {
      content: "";
      position: relative;
      display: block;
      width: 8px;
      height: 2px;
      background: #fff;
      margin-left: 3px;
      margin-top: 6px;
    }

    &:focus {
      outline: 2px solid #35baf6;
      outline-offset: 2px;
    }

    &:disabled {
      background: #f9fafb;
      border-color: #e5e7eb;
      cursor: not-allowed;
    }
  }

  // ── PRINT QTY INPUT & DISPLAY ──
  .lp-batch-total-qty {
    padding-right: unset !important;
    display: inline-block;
    // width: 58px;
    padding: 4px 6px;
    text-align: right;
    font-size: 12px;
    color: #6b7280;
    font-weight: 500;
  }

  .gcorp-grid-input {
    width: 58px;
    height: 28px;
    padding: 4px 6px;
    text-align: right;
    border: 1px solid #d0d5dd;
    border-radius: 4px;
    background: #fff;
    color: #101828;
    font-family: inherit;
    font-size: 12px;
    transition: border-color 0.15s ease;

    &:hover {
      border-color: #b3bac5;
    }

    &:focus {
      outline: none;
      border-color: #35baf6;
      box-shadow: 0 0 0 3px rgba(53, 186, 246, 0.1);
    }

    &:disabled {
      background: #f9fafb;
      color: #98a2b3;
      cursor: not-allowed;
      border-color: #e5e7eb;
    }

    // Chrome, Edge, Safari spinners
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    // Firefox spinner
    &[type="number"] {
      -moz-appearance: textfield;
    }
  }

  // ── FOOTER ──
  .lp-grid-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    border-top: 1px solid #e5e7eb;
    padding: 12px 16px;
    background: #fff;
    flex-shrink: 0;
  }

  // ── PRINT BUTTON (custom native button) ──
  .lp-print-btn {
    height: 28px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 16px;
    background: #03a9f4;
    color: #fff;
    border: 1px solid #03a9f4;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: inherit;

    &:hover:not(:disabled) {
      background: #0075a9;
      border-color: #0075a9;
    }

    &:active:not(:disabled) {
      background: #03a9f478;
    }

    &:focus:not(:disabled) {
      outline: none;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &:disabled {
      background: #03a9f478;
      border-color: #03a9f478;
      color: #fff;
      cursor: not-allowed;
    }

    i {
      font-size: 14px;
    }
  }

  // ── PRINT BADGE ──
  .lp-print-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    background: #1f2937;
    color: #fff;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    text-align: center;
  }

  // ── QUANTITY MODE COLUMNS ──
  .lp-col-numlabels {
    width: 80px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .lp-col-calcmethod {
    width: 180px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .lp-col-labelqty {
    width: 90px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // ── CALC METHOD TOGGLE ──
  .lp-calc-method-toggle {
    display: flex;
    gap: 2px;
  }

  .lp-calc-btn {
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 500;
    color: #374151;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s ease;
    font-family: inherit;

    &:hover:not(.is-active) {
      background: #f3f4f6;
      border-color: #9ca3af;
    }

    &.is-active {
      background: #03a9f4;
      border-color: #03a9f4;
      color: #fff;
      font-weight: 600;
    }

    &:active {
      background: #0288d1;
    }
  }

  .lp-calc-error {
    position: absolute;
    top: 100%;
    left: 0;
    font-size: 11px;
    color: #dc2626;
    white-space: nowrap;
    margin-top: 4px;
    background: #fef2f2;
    padding: 3px 6px;
    border-radius: 3px;
    border: 1px solid #fecaca;
  }

  // ── LABEL ROWS ──
  .lp-label-rows {
    background: #f9fafb;
  }

  .lp-label-row {
    min-height: 36px;
    background: #fafbfc;
    border-top: 1px solid #e5e7eb;

    .lp-col-product {
      padding-left: 24px;
    }

    &:hover {
      background: #f5f7fb;
    }
  }

  .lp-label-num {
    font-size: 11px;
    color: #6b7280;
    font-weight: 400;
  }
}
</style>
